// packages/shared-components/src/hooks/useKeyboardLayout.ts

import {
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import type { GetDefaultKeyLayoutParType } from "@eweadn-qiankun/shared-types";
import {
  SDK_KEYBOARD_MOUSE_TYPE,
  isBeiyingDevice8815X98Keyboard,
  isActiveDriverTab,
  isZhwxFnLayerSupported,
  isSdcxFnLayerSupported,
  ACTIVE_DRIVER_TAB_CHANGED_EVENT,
  getTabScopedEquipmentData,
  getDriverTabLayoutScopePrefix,
  RONGYUAN_UI_DISABLED,
} from "@eweadn-qiankun/shared-utils";
import type { KeyboardSDK } from "../types/sdk";
import type { KeyConfig, KeyboardType } from "../components/Keyboard/types";
import {
  convertLayoutRows,
  generateDefaultLayout,
  getKeyWidthsByType,
  getKeyboardLayoutRowCount,
  normalizeDk63SdkKeycodeRow,
  patchLayoutKeyAtPosition,
} from "../utils/keyboardLayoutUtils";
import {
  applyZhwxDk87FnLayerToSdkRows,
  type ZhwxSdkRowLayout,
} from "../utils/zhwxDk87FnLayerMatrix";
import {
  applySdcxFnLayerToSdkRows,
  type SdcxSdkRowLayout,
} from "../utils/sdcxFnLayerMatrix";
import {
  buildHfdStaticLayoutRows,
  resolveHfdVirtualKeyboardLayout,
  isHfdDecodedLayoutUntrusted,
} from "../utils/keyCodeMapperHfd";
import {
  getHfdTrustedLayoutRows,
  setHfdTrustedLayoutRows,
  patchHfdTrustedLayoutCell,
} from "../utils/hfdTrustedLayoutCache";
import {
  isHuafenda24GKeyboard,
  isDakaiEquipment,
  waitForHuafendaSubAppSdkReady,
  waitForHuafendaConnectIdle,
  runHfdLayoutKeyDataSerialized,
  logVirtualKeyboardLoadPerf,
} from "@eweadn-qiankun/shared-utils";
import { KEYBOARD_LAYOUTS } from "../utils/keyCodeMapper";
import { getInitializeKeyboard } from "../utils/keyCodeMapperBeiying";
import {
  KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT,
  useOwnerResyncParticipant,
  fingerprintJson,
  isOwnerResyncLoading,
} from "../utils/deviceSessionOwnerResync";
import { mergeOwnerResyncSnapshotFingerprint } from "../utils/ownerResyncSnapshot";
import { formatGamma75KeyLayout } from "../utils/gamma75FnLayerMatrix";

export interface SDKRowLayout {
  layer: number;
  row: number;
  keyboardLayout: number[];
  keyboardData?: any;
  ledBeads?: any;
}

/**
 * 贝盈：无 web-hid remapping 或未挂载 beiyingKeyboard 时，用 KEYBOARD_LAYOUTS 静态矩阵生成行数据。
 */
function buildBeiyingStaticRowsFromLayoutMatrix(
  layerNum: number,
  kbType: string
): SDKRowLayout[] {
  const matrix = KEYBOARD_LAYOUTS[kbType as keyof typeof KEYBOARD_LAYOUTS];
  if (!matrix?.length) return [];
  return matrix.map((row, rowIndex) => ({
    layer: layerNum,
    row: rowIndex,
    keyboardLayout: row.map((cell) => (cell === 0 ? 0 : cell)),
  }));
}

function readEquipmentSnapshot(): {
  solutionRovider?: string;
  keyboardType?: string;
  vendorId?: number;
  productId?: number;
  machineBoard?: string;
} {
  try {
    const ed = getTabScopedEquipmentData();
    if (!ed || !Object.keys(ed).length) return {};
    return {
      solutionRovider: ed?.solutionRovider as string | undefined,
      keyboardType: ed?.keyboardType as string | undefined,
      vendorId: ed?.vendorId as number | undefined,
      productId: ed?.productId as number | undefined,
      machineBoard: ed?.machineBoard as string | undefined,
    };
  } catch {
    return {};
  }
}

/** equipmentData 尚未写入时，可用连接页写入的 solutionRoviderName */
function isRongyuanFnLayoutCacheUsable(storedRows: unknown): boolean {
  if (!Array.isArray(storedRows) || storedRows.length === 0) return false;
  return storedRows.some((row) => {
    const layout = (row as { keyboardLayout?: number[] })?.keyboardLayout;
    if (!Array.isArray(layout)) return false;
    return layout.some(
      (code) => code != null && code !== 0 && code !== RONGYUAN_UI_DISABLED
    );
  });
}

function resolveSolutionRovider(): string | undefined {
  const { solutionRovider } = readEquipmentSnapshot();
  if (solutionRovider) return String(solutionRovider);
  const name = localStorage.getItem("solutionRoviderName");
  return name || undefined;
}

/**
 * 子应用 zustand 可能尚未同步时，主应用已在 window.sdkApi 上挂载完整 SDK。
 */
function getEffectiveKeyboardSdk(
  sdkApi?: KeyboardSDK | null
): KeyboardSDK | undefined | null {
  if (sdkApi?.sdkKeyboardLayoutKeyRemappingApi) return sdkApi;
  if (typeof window === "undefined") return sdkApi;
  const w = window as unknown as { sdkApi?: { keyboard?: KeyboardSDK } };
  const fromWindow = w.sdkApi?.keyboard;
  if (fromWindow?.sdkKeyboardLayoutKeyRemappingApi) return fromWindow;
  return sdkApi ?? fromWindow;
}

/** 智慧无限 Fn 层：读取普通层行数据，供 Fn 空槽回退物理键帽（对齐华奋达 X87 V2） */
async function loadZhwxDk87BaseRowsForFnLayer(
  layoutApi: NonNullable<KeyboardSDK["sdkKeyboardLayoutKeyRemappingApi"]>,
  system: number,
  totalRows: number,
  cacheKeyboardType: KeyboardType
): Promise<ZhwxSdkRowLayout[]> {
  try {
    const cacheKey = getKeyboardLayoutCacheKey(cacheKeyboardType, 0, system);
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached) as ZhwxSdkRowLayout[];
      if (Array.isArray(parsed) && parsed.length >= totalRows) {
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }

  const baseRows: ZhwxSdkRowLayout[] = [];
  for (let i = 0; i < totalRows; i++) {
    const response = await layoutApi.getKeyLayout({ layer: 0, row: i, system });
    if ([200, 0].includes(response.code) && response.data) {
      const layoutItem = response.data as {
        row?: number;
        keyboardLayout?: number[];
      };
      baseRows.push({
        layer: 0,
        row: layoutItem.row ?? i,
        keyboardLayout: layoutItem.keyboardLayout ?? [],
      });
    }
  }
  return baseRows;
}

/** SDCX Fn 层：读取普通层行数据，供 Fn 空槽回退物理键帽 */
async function loadSdcxBaseRowsForFnLayer(
  layoutApi: NonNullable<KeyboardSDK["sdkKeyboardLayoutKeyRemappingApi"]>,
  system: number,
  totalRows: number
): Promise<SdcxSdkRowLayout[]> {
  const baseRows: SdcxSdkRowLayout[] = [];
  for (let i = 0; i < totalRows; i++) {
    const response = await layoutApi.getKeyLayout({ layer: 0, row: i, system });
    if ([200, 0].includes(response.code) && response.data) {
      const layoutItem = response.data as {
        row?: number;
        keyboardLayout?: number[];
      };
      baseRows.push({
        layer: 0,
        row: layoutItem.row ?? i,
        keyboardLayout: layoutItem.keyboardLayout ?? [],
      });
    }
  }
  return baseRows;
}

function getKeyboardLayoutCacheKey(
  keyboardType: KeyboardType,
  layer: number,
  system: number
): string {
  const eq = readEquipmentSnapshot();
  const solution =
    eq.solutionRovider ||
    localStorage.getItem("solutionRoviderName") ||
    "unknown";
  const vendorId = Number(eq.vendorId ?? 0);
  const productId = Number(eq.productId ?? 0);
  const board = String(eq.machineBoard || "na");
  const tabScope = getDriverTabLayoutScopePrefix();
  return `${tabScope}keyboardLayoutRows:${solution}:${vendorId}:${productId}:${board}:${keyboardType}:layer${layer}:system${system}`;
}

/** 历史键 keyboardLayoutRows 无设备维度，仅当与当前 equipment 一致时才允许读取 */
function legacyLayoutCacheMatchesCurrentEquipment(
  equipmentData: Record<string, unknown>,
  layoutKeyboardType: string
): boolean {
  const sol = String(equipmentData.solutionRovider ?? "").trim();
  const vid = Number(equipmentData.vendorId);
  const pid = Number(equipmentData.productId);
  if (!sol || !Number.isFinite(vid) || !Number.isFinite(pid)) return false;
  const board = String(equipmentData.machineBoard ?? "na");
  const kt = String(equipmentData.keyboardType ?? layoutKeyboardType).trim();
  const expectedPrefix = `keyboardLayoutRows:${sol}:${vid}:${pid}:${board}:${kt}:`;
  return getKeyboardLayoutCacheKey(
    (kt || layoutKeyboardType) as KeyboardType,
    0,
    0
  ).startsWith(expectedPrefix);
}

/**
 * 清除当前 equipmentData 对应设备下所有布局行缓存（含各 keyboardType / layer / system）。
 * 用于实体恢复默认键位后强制走设备拉取，避免与「未传 remapped 的 loadLayout」并发时仍读到旧缓存。
 */
export function clearKeyboardLayoutStorageCacheForCurrentDevice(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem("keyboardLayoutRows");
    const eq = readEquipmentSnapshot();
    /** 与 getKeyboardLayoutCacheKey 一致，避免删不到已写入的键 */
    const solution =
      eq.solutionRovider ||
      localStorage.getItem("solutionRoviderName") ||
      "unknown";
    const vendorId = Number(eq.vendorId ?? 0);
    const productId = Number(eq.productId ?? 0);
    const board = String(eq.machineBoard || "na");
    const tabScope = getDriverTabLayoutScopePrefix();
    const prefix = `${tabScope}keyboardLayoutRows:${solution}:${vendorId}:${productId}:${board}:`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    console.warn(
      "[useKeyboardLayout] clearKeyboardLayoutStorageCacheForCurrentDevice:",
      e
    );
  }
}

/** 设备侧（如星闪 Fn+Caps 恢复键位）触发后，主应用派发该事件；各页 useKeyboardLayout 监听并 remapped 重拉布局 */
export { KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT } from "../utils/deviceSessionOwnerResync";

export interface UseKeyboardLayoutOptions {
  /** SDK API 实例 */
  sdkApi?: KeyboardSDK;
  /** 键盘类型，默认 '68' */
  keyboardType?: KeyboardType;
  /** 键盘层数，默认 0 */
  layer?: number;
  /** 系统类型，0=Windows，1=macOS，默认 0（与 Win/Mac Base 对应） */
  system?: number;
  /** 是否自动加载，默认 true */
  autoLoad?: boolean;
  /** 总行数（可选，不传则根据键盘类型自动确定） */
  totalRows?: number;
  /**
   * 设备快照（如全局音乐律动预览 Tab 的 equipmentData）。
   * 与 preferStaticLayout 联用时按快照推导矩阵，不读 localStorage / SDK 当前绑定设备。
   */
  equipmentSnapshot?: Record<string, unknown>;
  /**
   * 为 true 且提供 equipmentSnapshot 时，仅用默认静态矩阵展示虚拟键盘，
   * 避免多设备场景下 localStorage 落在另一台实体导致预览 Tab 与矩阵型号不一致。
   */
  preferStaticLayout?: boolean;
  /** 加载成功回调 */
  onLoadSuccess?: (layout: KeyConfig[][]) => void;
  /** 加载失败回调 */
  onError?: (error: Error) => void;
}

export interface UseKeyboardLayoutResult {
  /** 键盘布局数据 */
  layout: KeyConfig[][] | null;
  /** 是否正在加载 */
  isLoading: boolean;
  /** 错误信息 */
  error: Error | null;
  /** 刷新布局 */
  refresh: (remapped?: boolean) => Promise<void | KeyConfig[][] | null>;
  /** 切换层 */
  switchLayer: (system: number, layer: number) => void;

  // ✅ 改键功能
  /** 获取指定位置的按键编码 */
  getKeyCode: (row: number, col: number) => Promise<number | null>;
  /** 设置指定位置的按键编码 */
  setKeyCode: (row: number, col: number, keycode: number) => Promise<boolean>;
  /** 恢复默认布局 */
  resetToDefault: (system?: 0 | 1) => Promise<boolean>;
  /** 是否正在修改按键 */
  isModifying: boolean;
}

/** 与 publish / 远程同步共用 getKeyWidthsByType 行数 */
const getDefaultTotalRows = (keyboardType: KeyboardType): number =>
  getKeyboardLayoutRowCount(keyboardType);

/** 仅 VID+PID 命中的 X98 8815（与 web-hid X98 8K 区分） */
function isBeiyingX98OneK(equipment: any): boolean {
  return isBeiyingDevice8815X98Keyboard(equipment);
}

/** ES98 等贝盈 web-hid：改键可走局部 UI 更新，不必每次全量 getKeymaps */
function isBeiyingWebHidRemapping(equipment: Record<string, unknown>): boolean {
  if (String(equipment.solutionRovider ?? "") !== "beiying") return false;
  const kt = String(equipment.keyboardType ?? "");
  if (kt === "m80" || kt === "m71" || kt === "t87") return false;
  if (isBeiyingDevice8815X98Keyboard(equipment)) return false;
  return true;
}

function patchKeyboardLayoutCacheCell(
  layoutKeyboardType: KeyboardType,
  layer: number,
  system: number,
  row: number,
  col: number,
  keycode: number
): void {
  try {
    const cacheKey = getKeyboardLayoutCacheKey(
      layoutKeyboardType,
      layer,
      system
    );
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return;
    const rows = JSON.parse(raw) as SDKRowLayout[];
    const target = rows.find((r) => r.row === row);
    if (!target || !Array.isArray(target.keyboardLayout)) return;
    if (col < 0 || col >= target.keyboardLayout.length) return;
    target.keyboardLayout[col] = keycode;
    localStorage.setItem(cacheKey, JSON.stringify(rows));
    if (layer === 0 && system === 0) {
      localStorage.setItem("keyboardLayoutRows", JSON.stringify(rows));
    }
  } catch {
    /* ignore cache patch errors */
  }
}

function resolveBeiyingVirtualKeyboardType(
  equipmentData: Record<string, unknown> | null | undefined,
  hookKeyboardType: KeyboardType
): KeyboardType {
  if (String(equipmentData?.solutionRovider ?? "") !== "beiying") {
    return hookKeyboardType;
  }
  if (isBeiyingDevice8815X98Keyboard(equipmentData)) {
    return "X98_1K";
  }
  const fromEq = String(equipmentData?.keyboardType ?? "").trim();
  if (fromEq) {
    return fromEq as KeyboardType;
  }
  return hookKeyboardType;
}

/**
 * 键盘布局管理 Hook
 *
 * @example
 * ```tsx
 * const { layout, isLoading, getKeyCode, setKeyCode } = useKeyboardLayout({
 *   sdkApi,
 *   keyboardType: '87',
 *   layer: 0,
 * })
 * ```
 */
export function useKeyboardLayout(
  options: UseKeyboardLayoutOptions = {}
): UseKeyboardLayoutResult {
  const {
    sdkApi,
    keyboardType = "68",
    layer: initialLayer = 0,
    system: initialSystem = 0,
    autoLoad = true,
    totalRows,
    equipmentSnapshot,
    preferStaticLayout = false,
    onLoadSuccess,
    onError,
  } = options;
  const actualTotalRows = totalRows ?? getDefaultTotalRows(keyboardType);
  const [layer, setLayer] = useState(initialLayer);
  const [system, setSystem] = useState<number>(initialSystem);
  const [layout, setLayout] = useState<KeyConfig[][] | null>(null);
  const layoutRef = useRef<KeyConfig[][] | null>(null);
  layoutRef.current = layout;
  /** 型号切换时先清空矩阵，避免短时间内在 Canvas 上画出上一款键盘再被替换造成闪屏 */
  const prevKeyboardTypeForClearRef = useRef(keyboardType);
  /** 本 hook 实例只上报一次虚拟键盘加载（进页 autoLoad + refresh 事件会多次 loadLayout） */
  const vkPerfReportedRef = useRef(false);
  useLayoutEffect(() => {
    if (prevKeyboardTypeForClearRef.current !== keyboardType) {
      prevKeyboardTypeForClearRef.current = keyboardType;
      setLayout(null);
      vkPerfReportedRef.current = false;
    }
  }, [keyboardType]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModifying, setIsModifying] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 加载键盘布局数据
   * @param options.silent 为 true 时不写 React state，返回布局供 Owner 协调器探测
   */
  const loadLayout = useCallback(
    async (
      remapped: boolean = false,
      options?: { silent?: boolean }
    ): Promise<KeyConfig[][] | null | void> => {
      const silent = options?.silent === true;
      const perfStartMs = Date.now();
      const reportVkPerf = (success: boolean, reason?: string) => {
        if (silent) return;
        if (vkPerfReportedRef.current) return;
        vkPerfReportedRef.current = true;
        try {
          const moduleId =
            typeof window !== "undefined"
              ? window.location.pathname.replace(/^\//, "") || undefined
              : undefined;
          logVirtualKeyboardLoadPerf({
            durationMs: Date.now() - perfStartMs,
            success,
            moduleId,
            reason,
            extra: { remapped },
          });
        } catch {
          // ignore
        }
      };
      const applyLayout = (keyConfigLayout: KeyConfig[][]) => {
        const layoutFp = fingerprintJson(
          keyConfigLayout.map((row) => row.map((k) => k.keyCode))
        );
        if (silent) {
          return keyConfigLayout;
        }
        setLayout(keyConfigLayout);
        onLoadSuccess?.(keyConfigLayout);
        mergeOwnerResyncSnapshotFingerprint("keyboard-layout", layoutFp);
        reportVkPerf(true);
        return keyConfigLayout;
      };
      /** Standby 不读 HID；layout 仅由远程同步事件更新，避免清缓存后布局错位 */
      if (
        typeof window !== "undefined" &&
        window.__eweadn_deviceSessionStandby
      ) {
        setIsLoading(false);
        return null;
      }

      if (!silent && !isActiveDriverTab()) {
        setIsLoading(false);
        return null;
      }

      if (remapped) {
        clearKeyboardLayoutStorageCacheForCurrentDevice();
      }

      if (preferStaticLayout) {
        let snap: Record<string, unknown> | null | undefined =
          equipmentSnapshot ?? undefined;
        if (!snap || typeof snap !== "object") {
          try {
            snap = getTabScopedEquipmentData();
          } catch {
            snap = null;
          }
        }
        if (snap && typeof snap === "object") {
          const layoutKeyboardType: KeyboardType =
            String(snap.solutionRovider ?? "") === "beiying"
              ? resolveBeiyingVirtualKeyboardType(snap, keyboardType)
              : keyboardType;
          const staticLayout = generateDefaultLayout(
            layoutKeyboardType,
            system
          );
          if (silent) return staticLayout;
          setLayout(staticLayout);
          onLoadSuccess?.(staticLayout);
          setIsLoading(false);
          reportVkPerf(true);
          return staticLayout;
        }
      }

      const effectiveSdk = getEffectiveKeyboardSdk(sdkApi);

      let equipmentData: Record<string, unknown> = {};
      try {
        equipmentData = getTabScopedEquipmentData();
      } catch {
        equipmentData = {};
      }
      let layoutKeyboardType: KeyboardType =
        String(equipmentData?.solutionRovider ?? "") === "beiying"
          ? resolveBeiyingVirtualKeyboardType(equipmentData, keyboardType)
          : keyboardType;

      if (!effectiveSdk?.sdkKeyboardLayoutKeyRemappingApi) {
        console.warn(
          "[useKeyboardLayout] sdkKeyboardLayoutKeyRemappingApi 未就绪，使用静态/默认布局兜底"
        );
        const sol = resolveSolutionRovider();
        if (sol === "beiying") {
          const kt = String(equipmentData.keyboardType || keyboardType || "68");
          const rows = buildBeiyingStaticRowsFromLayoutMatrix(layer, kt);
          const keyWidths = getKeyWidthsByType(layoutKeyboardType);
          const keyConfigLayout = convertLayoutRows(
            rows,
            keyWidths,
            layoutKeyboardType,
            layer,
            system
          );
          return applyLayout(keyConfigLayout);
        } else {
          const fallback = generateDefaultLayout(keyboardType, system);
          return applyLayout(fallback);
        }
        return null;
      }

      const cacheKey = getKeyboardLayoutCacheKey(
        layoutKeyboardType,
        layer,
        system
      );
      const expectedRowCount =
        totalRows ?? getDefaultTotalRows(layoutKeyboardType);
      let storedRows: any = [];
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          storedRows = JSON.parse(cached);
        } else if (layer === 0 && system === 0) {
          const legacy = localStorage.getItem("keyboardLayoutRows");
          if (
            legacy &&
            legacyLayoutCacheMatchesCurrentEquipment(
              equipmentData,
              String(layoutKeyboardType)
            )
          ) {
            storedRows = JSON.parse(legacy);
          }
        }
      } catch (err) {
        console.warn("[useKeyboardLayout] 读取缓存失败，将忽略缓存:", err);
        storedRows = [];
      }
      const isHuafendaLayout =
        String(equipmentData?.solutionRovider ?? "") === "huafenda";
      const isZhwxLayout =
        String(equipmentData?.solutionRovider ?? "") === "ZHWX";
      const isSdcxLayout =
        String(equipmentData?.solutionRovider ?? "") === "SDCX";
      const isRongyuanLayout =
        String(equipmentData?.solutionRovider ?? "") === "rongyuan";
      // 华奋达 / 智慧无限 / SDCX：不走 localStorage 布局缓存，须从设备矩阵实时回读
      if (isHuafendaLayout || isZhwxLayout || isSdcxLayout) {
        storedRows = [];
      }
      if (
        isRongyuanLayout &&
        layer === 1 &&
        !isRongyuanFnLayoutCacheUsable(storedRows)
      ) {
        storedRows = [];
      }
      let rows: SDKRowLayout[] = []; // 确保是 SDKRowLayout 类型

      const cacheRowCount = Array.isArray(storedRows) ? storedRows.length : 0;
      const cacheComplete =
        cacheRowCount > 0 && cacheRowCount >= expectedRowCount;
      if (
        !remapped &&
        cacheComplete &&
        !isHuafendaLayout &&
        !isZhwxLayout &&
        !isSdcxLayout &&
        !(
          isRongyuanLayout &&
          layer === 1 &&
          !isRongyuanFnLayoutCacheUsable(storedRows)
        )
      ) {
        try {
          rows = storedRows;
        } catch (err) {
          console.error("[useKeyboardLayout] localStorage 数据解析失败:", err);
          localStorage.removeItem(cacheKey);
        } finally {
          setIsLoading(false);
        }
      } else {
        if (!remapped && cacheRowCount > 0 && !cacheComplete) {
          console.warn(
            "[useKeyboardLayout] 忽略不完整布局缓存:",
            cacheRowCount,
            "期望行数",
            expectedRowCount
          );
          localStorage.removeItem(cacheKey);
          if (layer === 0 && system === 0) {
            localStorage.removeItem("keyboardLayoutRows");
          }
        }
        // 调用 API：加载中保持骨架，拿到布局再关 loading
        if (!silent) {
          setIsLoading(true);
        }
        setError(null);
        try {
          try {
            equipmentData = getTabScopedEquipmentData();
          } catch {
            equipmentData = {};
          }
          layoutKeyboardType =
            String(equipmentData?.solutionRovider ?? "") === "beiying"
              ? resolveBeiyingVirtualKeyboardType(equipmentData, keyboardType)
              : keyboardType;
          const solution =
            equipmentData.solutionRovider ?? resolveSolutionRovider();
          let resultKeyData: any = [];
          let hfdKeyDataIncomplete = false;
          let hfdUsedStaticLayoutFallback = false;
          if (solution !== "beiying") {
            if (equipmentData.solutionRovider === "huafenda") {
              const kbType = String(equipmentData.keyboardType || "");
              const layoutApi = effectiveSdk.sdkKeyboardLayoutKeyRemappingApi!;

              // Fn 层：静态 Fn 矩阵 / getFnKeyData，不走默认层 keyData 解码
              if (layer === 1) {
                for (let i = 0; i < actualTotalRows; i++) {
                  const params = { layer, row: i };
                  const response = await layoutApi.getKeyLayout(params);
                  if ([200, 0].includes(response.code) && response.data) {
                    const layoutItem: any = response.data;
                    rows.push({
                      layer: layoutItem.layer,
                      row: layoutItem.row,
                      keyboardLayout: layoutItem.keyboardLayout,
                      keyboardData: layoutItem.keyboardData,
                      ledBeads: layoutItem.ledBeads,
                    });
                  } else {
                    throw new Error(
                      `获取第 ${i} 行布局失败: ${response.message}`
                    );
                  }
                }
              } else {
                const getKeyData = () =>
                  effectiveSdk.sdkKeyboardBasicApi!.hfdServiceKeyboard.instance.getKeyData();
                const is24G = isHuafenda24GKeyboard(equipmentData);

                await waitForHuafendaSubAppSdkReady();
                await waitForHuafendaConnectIdle();

                // 策略：一次 getKeyData → 成功立刻用；失败有限次重试；仍失败再静态兜底（避免骨架永久转圈）
                const maxFailRetries = remapped ? 4 : 3;
                let resolved: Awaited<
                  ReturnType<typeof resolveHfdVirtualKeyboardLayout>
                > | null = null;
                for (let attempt = 0; attempt < maxFailRetries; attempt++) {
                  const isLast = attempt === maxFailRetries - 1;
                  resolved = await runHfdLayoutKeyDataSerialized(() =>
                    resolveHfdVirtualKeyboardLayout({
                      getKeyData,
                      keyboardType: kbType,
                      layer,
                      totalRows: actualTotalRows,
                      // 单次尝试少读：有线 1 次，2.4G 最多 2 次；失败由外层重试
                      maxReadAttempts: is24G ? 2 : 1,
                      maxLayoutAttempts: 1,
                      retryDelayMs: is24G ? 120 : 0,
                      allowStaticFallback: isLast && !remapped,
                    })
                  );
                  const ok =
                    Boolean(resolved.rows?.length) &&
                    !resolved.discardResult &&
                    !resolved.incomplete &&
                    !isHfdDecodedLayoutUntrusted(resolved.rows, kbType);
                  if (ok || (isLast && resolved.rows?.length)) {
                    break;
                  }
                  if (!isLast) {
                    await new Promise((r) => setTimeout(r, 200));
                  }
                }

                if (
                  !resolved ||
                  resolved.discardResult ||
                  !resolved.rows?.length
                ) {
                  console.warn(
                    "[useKeyboardLayout] HFD 布局获取失败，保持骨架/当前界面",
                    { kbType }
                  );
                  if (!silent) setIsLoading(false);
                  return null;
                }

                rows = resolved.rows as SDKRowLayout[];
                resultKeyData = resolved.keyData;
                hfdKeyDataIncomplete = Boolean(resolved.incomplete);
                hfdUsedStaticLayoutFallback = Boolean(
                  resolved.usedStaticFallback && !resolved.usedTrustedCache
                );

                if (resolved.usedShadow) {
                  localStorage.setItem("HFD_SHADOW_BUFFER_READY_KEY", "1");
                } else {
                  localStorage.removeItem("HFD_SHADOW_BUFFER_READY_KEY");
                }

                if (
                  rows.length &&
                  !hfdKeyDataIncomplete &&
                  !isHfdDecodedLayoutUntrusted(rows, kbType)
                ) {
                  setHfdTrustedLayoutRows(kbType, layer, rows);
                }
              }
            } else if (solution === "dakai") {
              // V80：先静态布局结束骨架，再并行拉真实行（矩阵读取有 in-flight 合并）
              if (!silent && !remapped) {
                const quick = generateDefaultLayout(
                  layoutKeyboardType as KeyboardType,
                  system
                );
                setLayout(quick);
                setIsLoading(false);
              }
              const api = effectiveSdk.sdkKeyboardLayoutKeyRemappingApi!;
              const rowResponses = await Promise.all(
                Array.from({ length: expectedRowCount }, (_, i) =>
                  api.getKeyLayout({ layer, row: i, system })
                )
              );
              for (let i = 0; i < rowResponses.length; i++) {
                const response = rowResponses[i];
                if ([200, 0].includes(response.code) && response.data) {
                  const layoutItem: any = response.data;
                  rows.push({
                    layer: layoutItem.layer,
                    row: layoutItem.row,
                    keyboardLayout: layoutItem.keyboardLayout,
                    keyboardData: layoutItem.keyboardData,
                    ledBeads: layoutItem.ledBeads,
                  });
                } else {
                  throw new Error(
                    `获取第 ${i} 行布局失败: ${response?.message ?? ""}`
                  );
                }
              }
            } else {
              // 逐行获取布局（ZHWX / SDCX / 容园 / 其它非华奋达/达克）
              const layoutApi = effectiveSdk.sdkKeyboardLayoutKeyRemappingApi!;
              const rowResponses = await Promise.all(
                Array.from({ length: actualTotalRows }, (_, i) =>
                  layoutApi.getKeyLayout(
                    { layer, row: i, system },
                    resultKeyData
                  )
                )
              );
              for (let i = 0; i < rowResponses.length; i++) {
                const response = rowResponses[i];
                if ([200, 0].includes(response.code) && response.data) {
                  const layoutItem: any = response.data;
                  rows.push({
                    layer: layoutItem.layer,
                    row: layoutItem.row,
                    keyboardLayout: layoutItem.keyboardLayout,
                    keyboardData: layoutItem.keyboardData,
                    ledBeads: layoutItem.ledBeads,
                  });
                } else {
                  throw new Error(
                    `获取第 ${i} 行布局失败: ${response?.message ?? ""}`
                  );
                }
              }
            }

            // 智慧无限 Fn 层：空槽/未改键格回退普通层键帽文案（DK75 Single 无 Fn 层）
            if (
              solution === "ZHWX" &&
              layer === 1 &&
              isZhwxFnLayerSupported(String(layoutKeyboardType)) &&
              rows.length
            ) {
              const baseRows = await loadZhwxDk87BaseRowsForFnLayer(
                effectiveSdk.sdkKeyboardLayoutKeyRemappingApi!,
                system,
                actualTotalRows,
                layoutKeyboardType
              );
              rows = applyZhwxDk87FnLayerToSdkRows(rows, baseRows);
            }

            // SDCX Fn 层：空槽回退普通层键帽文案（不区分 Win/Mac）
            if (
              solution === "SDCX" &&
              layer === 1 &&
              isSdcxFnLayerSupported(String(layoutKeyboardType)) &&
              rows.length
            ) {
              const baseRows = await loadSdcxBaseRowsForFnLayer(
                effectiveSdk.sdkKeyboardLayoutKeyRemappingApi!,
                system,
                actualTotalRows
              );
              rows = applySdcxFnLayerToSdkRows(rows, baseRows);
            }
          } else if (solution === "beiying") {
            const basicApi = effectiveSdk.sdkKeyboardBasicApi as any;
            const beiyingKb = basicApi?.beiyingKeyboard;
            const beiyingX98OneK = basicApi?.beiyingKeyboardX98_1K_8815;
            const isBeiyingMachineKeyboard =
              Number(equipmentData.sdkType) ===
                SDK_KEYBOARD_MOUSE_TYPE.keyboard.beiyingMachine ||
              equipmentData.machineBoard === "916" ||
              equipmentData.keyboardType === "m80" ||
              equipmentData.keyboardType === "m71" ||
              equipmentData.keyboardType === "t87" ||
              equipmentData.keyboardType === "x75v2" ||
              equipmentData.keyboardType === "m75";

            if (isBeiyingMachineKeyboard) {
              // 机甲改键回显：多行并发 getKeyLayout。SDK 内对同一次 getKeyboardKeys
              // 有 in-flight 合并，比逐行 await 可显著减少 HID 等待。
              const api = effectiveSdk.sdkKeyboardLayoutKeyRemappingApi!;
              const rowResponses = await Promise.all(
                Array.from({ length: expectedRowCount }, (_, i) =>
                  api.getKeyLayout({ layer, row: i, system })
                )
              );
              for (let i = 0; i < rowResponses.length; i++) {
                const response = rowResponses[i];
                if ([200, 0].includes(response.code) && response.data) {
                  const layoutItem: any = response.data;
                  rows.push({
                    layer: layoutItem.layer,
                    row: layoutItem.row,
                    keyboardLayout: layoutItem.keyboardLayout,
                    keyboardData: layoutItem.keyboardData,
                    ledBeads: layoutItem.ledBeads,
                  });
                } else {
                  throw new Error(
                    `获取第 ${i} 行布局失败: ${response.message}`
                  );
                }
              }
            }

            // X98(8815)：与机甲一致走 getKeyLayout → getKeyCode，才能把空格等格上的宏回显为 62720+（勿仅用 getInitializeKeyboard 原样 value）
            const x98OneKUseKeyLayout =
              isBeiyingX98OneK(equipmentData) &&
              beiyingX98OneK?.instance != null &&
              effectiveSdk.sdkKeyboardLayoutKeyRemappingApi;
            if (x98OneKUseKeyLayout) {
              const api = effectiveSdk.sdkKeyboardLayoutKeyRemappingApi!;
              const rowResponses = await Promise.all(
                Array.from({ length: expectedRowCount }, (_, i) =>
                  api.getKeyLayout({ layer, row: i, system })
                )
              );
              for (let i = 0; i < rowResponses.length; i++) {
                const response = rowResponses[i];
                if ([200, 0].includes(response.code) && response.data) {
                  const layoutItem: any = response.data;
                  rows.push({
                    layer: layoutItem.layer,
                    row: layoutItem.row,
                    keyboardLayout: layoutItem.keyboardLayout,
                    keyboardData: layoutItem.keyboardData,
                    ledBeads: layoutItem.ledBeads,
                  });
                } else {
                  throw new Error(
                    `获取第 ${i} 行布局失败: ${response.message}`
                  );
                }
              }
            }

            // 机甲键盘走 @bytech/web-sdk-machine，与 WebHID remapping.getInitializeKeyboard 协议不同；
            // 若误用会长时间轮询或 await 永不返回，灯光页全屏 Loading 卡在 99%。此处仅用静态 KEYBOARD_LAYOUTS。
            const activeBeiyingKeyboard =
              beiyingX98OneK?.instance != null ? beiyingX98OneK : beiyingKb;
            if (
              !isBeiyingMachineKeyboard &&
              !x98OneKUseKeyLayout &&
              activeBeiyingKeyboard?.instance != null
            ) {
              const isEs87 =
                String(equipmentData.keyboardType ?? "") === "es87";
              const isEs98Nano24G =
                String(equipmentData.keyboardType ?? "") === "99" &&
                Number(equipmentData.productId) === 0x10d5;
              const LAYOUT_INIT_MS = isEs87 || isEs98Nano24G ? 20_000 : 8_000;

              try {
                rows = await Promise.race([
                  getInitializeKeyboard(layer, activeBeiyingKeyboard, system),
                  new Promise<never>((_, reject) =>
                    setTimeout(
                      () =>
                        reject(
                          new Error(
                            "[useKeyboardLayout] getInitializeKeyboard 超时"
                          )
                        ),
                      LAYOUT_INIT_MS
                    )
                  ),
                ]);
              } catch (beiyingInitErr: any) {
                console.warn(
                  "[useKeyboardLayout] getInitializeKeyboard 失败，将使用布局矩阵兜底:",
                  beiyingInitErr
                );
              }
            }
            if (!rows?.length) {
              const kt = String(
                equipmentData.keyboardType || keyboardType || "68"
              );
              rows = buildBeiyingStaticRowsFromLayoutMatrix(layer, kt);
            }
          }
          // 更新缓存（改键后 refresh(true) 会覆盖缓存）
          if (!hfdKeyDataIncomplete && !hfdUsedStaticLayoutFallback) {
            localStorage.setItem(cacheKey, JSON.stringify(rows));
            if (layer === 0 && system === 0) {
              // 兼容历史读取方
              localStorage.setItem("keyboardLayoutRows", JSON.stringify(rows));
            }
          }
        } catch (err: any) {
          console.error("[useKeyboardLayout] 加载键盘布局失败:", err);
          setError(err);
          onError?.(err);
          const sol = resolveSolutionRovider();
          if (sol === "beiying") {
            let eqErr: Record<string, unknown> = {};
            try {
              eqErr = getTabScopedEquipmentData();
            } catch {
              eqErr = {};
            }
            const renderOnErr =
              String(eqErr?.solutionRovider ?? "") === "beiying"
                ? resolveBeiyingVirtualKeyboardType(eqErr, keyboardType)
                : keyboardType;
            const kt = String(eqErr.keyboardType || keyboardType || "68");
            const fallbackRows = buildBeiyingStaticRowsFromLayoutMatrix(
              layer,
              kt
            );
            const keyWidths = getKeyWidthsByType(renderOnErr);
            const keyConfigLayout = convertLayoutRows(
              fallbackRows,
              keyWidths,
              renderOnErr,
              layer,
              system
            );
            setLayout(keyConfigLayout);
            onLoadSuccess?.(keyConfigLayout);
            if (silent) return keyConfigLayout;
          } else {
            const fallback = generateDefaultLayout(keyboardType, system);
            setLayout(fallback);
            onLoadSuccess?.(fallback);
            if (silent) return fallback;
          }
          return null;
        } finally {
          setIsLoading(false);
        }
      }
      try {
        equipmentData = getTabScopedEquipmentData();
      } catch {
        equipmentData = {};
      }
      layoutKeyboardType =
        String(equipmentData?.solutionRovider ?? "") === "beiying"
          ? resolveBeiyingVirtualKeyboardType(equipmentData, keyboardType)
          : keyboardType;
      const keyWidths = getKeyWidthsByType(layoutKeyboardType);
      let keyConfigLayout = convertLayoutRows(
        rows,
        keyWidths,
        layoutKeyboardType,
        layer,
        system
      );

      // FIX: 处理 Gamma75 Fn/Fn2 层布局偏移（仅调用兼容方法，不改 formatGamma75KeyLayout 本体）
      keyConfigLayout =
        layoutKeyboardType === "gamma75" && layer > 0
          ? formatGamma75KeyLayout(keyConfigLayout)
          : keyConfigLayout;
      return applyLayout(keyConfigLayout);
    },
    [
      sdkApi,
      keyboardType,
      layer,
      system,
      actualTotalRows,
      equipmentSnapshot,
      preferStaticLayout,
      onLoadSuccess,
      onError,
    ]
  );
  /**
   * 切换键盘层
   */
  const switchLayer = useCallback(
    (newSystem: number, newLayer: number) => {
      if (newSystem !== system) {
        setSystem(newSystem);
      }
      if (newLayer !== layer) {
        // 确保新层与当前层不同
        setLayer(newLayer);
      }
    },
    [layer, system]
  );

  /**
   * 获取指定位置的按键编码
   */
  const getKeyCode = useCallback(
    async (row: number, col: number): Promise<number | null> => {
      const effectiveSdk = getEffectiveKeyboardSdk(sdkApi);
      if (!effectiveSdk?.sdkKeyboardLayoutKeyRemappingApi) {
        console.error("[useKeyboardLayout] SDK API 未初始化");
        return null;
      }

      try {
        const response =
          await effectiveSdk.sdkKeyboardLayoutKeyRemappingApi.getKeyCode({
            layer,
            system,
            row,
            col,
          });

        if (response.code === 200 && response.data) {
          return response.data.keycode;
        } else {
          console.error(
            "[useKeyboardLayout] 获取按键编码失败:",
            response.message
          );
          return null;
        }
      } catch (err: any) {
        console.error("[useKeyboardLayout] 获取按键编码异常:", err);
        return null;
      }
    },
    [sdkApi, layer, system]
  );

  /**
   * 设置指定位置的按键编码
   */
  const setKeyCode = useCallback(
    async (row: number, col: number, keycode: number): Promise<boolean> => {
      const effectiveSdk = getEffectiveKeyboardSdk(sdkApi);
      if (!effectiveSdk?.sdkKeyboardLayoutKeyRemappingApi) {
        console.error("[useKeyboardLayout] SDK API 未初始化");
        return false;
      }

      setIsModifying(true);

      try {
        const response =
          await effectiveSdk.sdkKeyboardLayoutKeyRemappingApi.setKeyCode({
            layer,
            row,
            col,
            keycode,
            system,
          });

        if (response.code === 200) {
          const equipmentData = readEquipmentSnapshot();
          const isHuafenda =
            String(equipmentData?.solutionRovider ?? "") === "huafenda";
          const isZhwx =
            String(equipmentData?.solutionRovider ?? "") === "ZHWX";
          const isSdcx =
            String(equipmentData?.solutionRovider ?? "") === "SDCX";
          const isRongyuan =
            String(equipmentData?.solutionRovider ?? "") === "rongyuan";
          const appliedRow = response.data?.row ?? row;
          const appliedCol = response.data?.col ?? col;
          const writeIgnored = response.data?.keyIndex === -1;
          if (writeIgnored) {
            return false;
          }
          const deviceApplied = response.data?.appliedToDevice !== false;
          if (
            deviceApplied &&
            (isBeiyingWebHidRemapping(equipmentData) ||
              isHuafenda ||
              isZhwx ||
              isSdcx ||
              isRongyuan)
          ) {
            const renderType =
              String(equipmentData?.solutionRovider ?? "") === "beiying"
                ? resolveBeiyingVirtualKeyboardType(equipmentData, keyboardType)
                : keyboardType;
            setLayout((prev) => {
              if (!prev) return prev;
              return patchLayoutKeyAtPosition(
                prev,
                appliedRow,
                appliedCol,
                keycode,
                renderType
              );
            });
            patchKeyboardLayoutCacheCell(
              renderType,
              layer,
              system,
              appliedRow,
              appliedCol,
              keycode
            );
            if (isRongyuan) {
              return true;
            }
            if (isZhwx || isSdcx) {
              return true;
            }
            if (isHuafenda) {
              // 同步跨页「可信改键」缓存，灯光/性能等页不得回退出厂默认
              const kbType = String(
                equipmentData.keyboardType || renderType || ""
              );
              try {
                const cacheKey = getKeyboardLayoutCacheKey(
                  renderType,
                  layer,
                  system
                );
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                  const parsed = JSON.parse(cached);
                  if (Array.isArray(parsed) && parsed.length) {
                    const target = parsed.find(
                      (r: { row?: number }) => Number(r.row) === Number(row)
                    );
                    if (target?.keyboardLayout) {
                      target.keyboardLayout[col] = keycode;
                    }
                    setHfdTrustedLayoutRows(kbType, layer, parsed);
                    localStorage.setItem(cacheKey, JSON.stringify(parsed));
                  }
                } else {
                  const existing = getHfdTrustedLayoutRows(kbType, layer);
                  const base = existing?.length
                    ? existing
                    : buildHfdStaticLayoutRows(kbType, layer);
                  const next = base.map((item) => {
                    if (Number(item.row) !== Number(row)) return item;
                    const layout = [...(item.keyboardLayout ?? [])];
                    if (col >= 0 && col < layout.length) {
                      layout[col] = Number(keycode);
                    }
                    return { ...item, keyboardLayout: layout };
                  });
                  setHfdTrustedLayoutRows(kbType, layer, next);
                  localStorage.setItem(cacheKey, JSON.stringify(next));
                }
              } catch {
                patchHfdTrustedLayoutCell(kbType, layer, row, col, keycode);
              }
              void loadLayout(true);
            }
            return true;
          }
          await loadLayout(true);
          return true;
        } else {
          console.error(
            "[useKeyboardLayout] 设置按键编码失败:",
            response.message
          );
          return false;
        }
      } catch (err: any) {
        console.error("[useKeyboardLayout] 设置按键编码异常:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [sdkApi, layer, system, loadLayout]
  );

  /**
   * 恢复默认布局：按层拉取默认键表后逐键写回，再强制从设备重载回显。
   * @param system 系统类型 (0: Windows, 1: macOS)。须与当前 UI 层一致；
   *   Mac 层传 0 会取 Win 默认表并写回当前设备层，导致修饰键/功能键错乱。
   */
  const resetToDefault = useCallback(
    async (system: 0 | 1 = 0): Promise<boolean> => {
      const effectiveSdk = getEffectiveKeyboardSdk(sdkApi);
      if (!effectiveSdk?.sdkKeyboardLayoutKeyRemappingApi) {
        console.error("[useKeyboardLayout] SDK API 未初始化");
        return false;
      }

      setIsModifying(true);
      const rows: SDKRowLayout[] = [];
      try {
        // 与 featrue/zjf/beiying-m71-1k 下 beiying1 一致：设备侧整表恢复（resetKeymaps / 8815 resetKeyboard），
        // 无逐行 keyCodes 返回；与逐键 setKeyCode 的 HFD 路径不同。
        const equipmentData = getTabScopedEquipmentData();
        const isRongyuan =
          String(equipmentData?.solutionRovider ?? "") === "rongyuan";
        if (
          isRongyuan &&
          typeof effectiveSdk.sdkKeyboardLayoutKeyRemappingApi
            .resetKeyLayout === "function"
        ) {
          const resetRes =
            await effectiveSdk.sdkKeyboardLayoutKeyRemappingApi.resetKeyLayout({
              fn: layer,
              layer,
              system,
            });
          if (resetRes.code !== 200) {
            console.error(
              "[useKeyboardLayout] 容园恢复默认失败",
              resetRes.message
            );
            return false;
          }
          const cacheKey = getKeyboardLayoutCacheKey(
            keyboardType,
            layer,
            system
          );
          localStorage.removeItem(cacheKey);
          if (layer === 0 && system === 0) {
            localStorage.removeItem("keyboardLayoutRows");
          }
          await loadLayout(true);
          return true;
        }

        if (isDakaiEquipment(equipmentData)) {
          const resetResponse =
            await effectiveSdk.sdkKeyboardLayoutKeyRemappingApi.resetToDefault?.(
              system
            );
          if (!resetResponse || resetResponse.code !== 200) {
            console.error(
              "[useKeyboardLayout] 达克恢复默认失败",
              resetResponse?.message
            );
            return false;
          }
          localStorage.removeItem("keyboardLayoutRows");
          await loadLayout(true);
          return true;
        }

        if (isBeiyingX98OneK(equipmentData)) {
          const defaultParams: GetDefaultKeyLayoutParType = {
            row: 0,
            fn: layer,
            system,
            allLayers: true,
          };
          const defaultLayoutResponse =
            await effectiveSdk.sdkKeyboardLayoutKeyRemappingApi.getDefaultKeyLayout(
              defaultParams
            );
          if (defaultLayoutResponse.code !== 200) {
            console.error(
              "[useKeyboardLayout] 贝盈 X98 恢复默认（整表）失败",
              defaultLayoutResponse.message
            );
            return false;
          }
          if (!defaultLayoutResponse.data) {
            return false;
          }
          localStorage.removeItem("keyboardLayoutRows");
          await loadLayout(true);
          return true;
        }

        if (String(equipmentData?.solutionRovider ?? "") === "ZHWX") {
          const resetApi =
            effectiveSdk.sdkKeyboardLayoutKeyRemappingApi
              .resetToDefaultKeyMatrix;
          if (!resetApi) {
            console.error(
              "[useKeyboardLayout] 智慧无限缺少 resetToDefaultKeyMatrix"
            );
            return false;
          }
          const resetResponse = await resetApi({
            layer,
            fn: layer,
            allLayers: true,
          });
          if (resetResponse.code !== 200) {
            console.error(
              "[useKeyboardLayout] 智慧无限恢复默认（整表）失败",
              resetResponse.message
            );
            return false;
          }
          localStorage.removeItem("keyboardLayoutRows");
          await loadLayout(true);
          return true;
        }

        for (let row = 0; row < actualTotalRows; row++) {
          const defaultLayoutResponse =
            await effectiveSdk.sdkKeyboardLayoutKeyRemappingApi.getDefaultKeyLayout(
              {
                row,
                fn: layer,
                system,
              }
            );
          if (
            defaultLayoutResponse.code !== 200 ||
            !defaultLayoutResponse.data
          ) {
            console.error(`[useKeyboardLayout] 获取第 ${row} 行默认布局失败`);
            continue;
          }

          // 华丰达 getDefaultKeyLayout 返回 getKeyLayout 结构（keyboardLayout）；类型约定为 keyCodes
          // 部分 SDK 曾误把整段 { code, data } 再包一层，兼容 data.data
          let rowData = defaultLayoutResponse.data as {
            keyCodes?: number[];
            keyboardLayout?: number[];
            fn?: number;
            layer?: number;
            row?: number;
            data?: {
              keyCodes?: number[];
              keyboardLayout?: number[];
              fn?: number;
              layer?: number;
              row?: number;
            };
          };
          if (
            rowData &&
            !Array.isArray(rowData.keyCodes) &&
            !Array.isArray(rowData.keyboardLayout) &&
            rowData.data &&
            (Array.isArray(rowData.data.keyCodes) ||
              Array.isArray(rowData.data.keyboardLayout))
          ) {
            rowData = rowData.data;
          }

          const defaultKeyCodes = Array.isArray(rowData.keyCodes)
            ? rowData.keyCodes
            : Array.isArray(rowData.keyboardLayout)
              ? rowData.keyboardLayout
              : null;

          if (!defaultKeyCodes) {
            console.error(
              `[useKeyboardLayout] 第 ${row} 行默认布局缺少 keyCodes/keyboardLayout，已跳过`
            );
            continue;
          }

          const rowWidths = getKeyWidthsByType(keyboardType)[row] ?? [];
          const normalizedDefaultCodes =
            keyboardType === "63"
              ? normalizeDk63SdkKeycodeRow(defaultKeyCodes, row, rowWidths)
              : defaultKeyCodes;

          rows.push({
            keyboardLayout: normalizedDefaultCodes,
            layer: rowData.fn ?? rowData.layer ?? layer,
            row: rowData.row ?? row,
          });
          const cacheKey = getKeyboardLayoutCacheKey(
            keyboardType,
            layer,
            system
          );
          localStorage.setItem(cacheKey, JSON.stringify(rows));
          if (layer === 0 && system === 0) {
            localStorage.setItem("keyboardLayoutRows", JSON.stringify(rows));
          }
          for (let col = 0; col < normalizedDefaultCodes.length; col++) {
            const keycode = normalizedDefaultCodes[col];
            if (keycode === 0) continue; // 跳过空键
            if (keyboardType === "63" && (rowWidths[col] ?? 0) === 0) {
              continue;
            }

            /**
             * 写回必须带上与 getDefaultKeyLayout 相同的 system，
             * 否则缺省按 Win 处理，Mac 层恢复会串层或键位错误。
             */
            await effectiveSdk.sdkKeyboardLayoutKeyRemappingApi.setKeyCode({
              layer,
              row,
              col,
              keycode,
              system,
            });
          }
        }

        // remapped=true：不走 localStorage 缓存，从设备重新拉取布局，避免仍显示改键前界面
        await loadLayout(true);
        return true;
      } catch (err: any) {
        console.error("[useKeyboardLayout] 恢复默认布局异常:", err);
        return false;
      } finally {
        setIsModifying(false);
      }
    },
    [sdkApi, layer, actualTotalRows, loadLayout]
  );

  useEffect(() => {
    if (autoLoad && layer !== null) {
      if (
        typeof window !== "undefined" &&
        window.__eweadn_deviceSessionStandby
      ) {
        return;
      }
      if (!isActiveDriverTab()) {
        return;
      }
      loadLayout();
    }
  }, [
    autoLoad,
    layer,
    system,
    loadLayout,
    keyboardType,
    equipmentSnapshot,
    preferStaticLayout,
  ]);

  /** Owner 接管：静默探测 + 稳定后一次性回显布局 */
  const probedLayoutRef = useRef<KeyConfig[][] | null>(null);
  const ownerLayoutResyncEnabled = Boolean(
    autoLoad && layer !== null && !preferStaticLayout
  );

  useOwnerResyncParticipant(
    {
      id: "keyboard-layout",
      probe: async () => {
        const result = await loadLayout(true, { silent: true });
        const layoutData = (result as KeyConfig[][] | null) ?? null;
        if (!layoutData?.length) {
          return { valid: false, fingerprint: "" };
        }
        probedLayoutRef.current = layoutData;
        return {
          valid: true,
          fingerprint: fingerprintJson(
            layoutData.map((row) => row.map((k) => k.keyCode))
          ),
        };
      },
      commit: async () => {
        const cached = probedLayoutRef.current;
        probedLayoutRef.current = null;
        if (cached?.length) {
          setLayout(cached);
          onLoadSuccess?.(cached);
          const layoutFp = fingerprintJson(
            cached.map((row) => row.map((k) => k.keyCode))
          );
          mergeOwnerResyncSnapshotFingerprint("keyboard-layout", layoutFp);
        } else {
          await loadLayout(true);
        }
      },
      getUiFingerprint: () => {
        const l = layoutRef.current;
        if (!l?.length) return null;
        return fingerprintJson(l.map((row) => row.map((k) => k.keyCode)));
      },
    },
    ownerLayoutResyncEnabled
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onDeviceLayoutRefresh = () => {
      if (
        typeof window !== "undefined" &&
        (window.__eweadn_deviceSessionStandby || isOwnerResyncLoading())
      ) {
        return;
      }
      if (!isActiveDriverTab()) return;
      void loadLayout(true);
    };
    /** 切回操作页：已有布局则静默读实体键位，指纹变化才更新虚拟键盘（避免整页 Loading） */
    const onActiveTabChanged = () => {
      if (!isActiveDriverTab() || !autoLoad) return;
      void (async () => {
        const hadLayout = !!layoutRef.current?.length;
        if (!hadLayout) {
          await loadLayout(true);
          return;
        }
        const result = await loadLayout(true, { silent: true });
        const next = (result as KeyConfig[][] | null) ?? null;
        if (!next?.length) return;
        const nextFp = fingerprintJson(
          next.map((row) => row.map((k) => k.keyCode))
        );
        const ui = layoutRef.current;
        const uiFp = ui?.length
          ? fingerprintJson(ui.map((row) => row.map((k) => k.keyCode)))
          : null;
        if (uiFp === nextFp) return;
        setLayout(next);
        onLoadSuccess?.(next);
        mergeOwnerResyncSnapshotFingerprint("keyboard-layout", nextFp);
      })();
    };
    window.addEventListener(
      KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT,
      onDeviceLayoutRefresh
    );
    window.addEventListener(
      ACTIVE_DRIVER_TAB_CHANGED_EVENT,
      onActiveTabChanged
    );
    return () => {
      window.removeEventListener(
        KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT,
        onDeviceLayoutRefresh
      );
      window.removeEventListener(
        ACTIVE_DRIVER_TAB_CHANGED_EVENT,
        onActiveTabChanged
      );
    };
  }, [loadLayout, autoLoad]);

  return {
    layout,
    isLoading,
    error,
    refresh: (remapped: boolean = false) => loadLayout(remapped),
    switchLayer,
    getKeyCode,
    setKeyCode,
    resetToDefault,
    isModifying,
  };
}
